import React, { useState, useEffect, useCallback } from 'react';
import { interaccionesAPI, clientesAPI } from '../services/api';
import './Interacciones.css';

const TIPOS = ['llamada', 'email', 'reunion', 'nota', 'tarea', 'cita', 'sesion', 'oportunidad', 'factura', 'otro'];

const TIPO_ICONS = {
  llamada: '📞',
  email: '📧',
  reunion: '🤝',
  nota: '📝',
  tarea: '✅',
  cita: '📅',
  sesion: '💬',
  oportunidad: '💰',
  factura: '🧾',
  otro: '📌',
};

function Interacciones() {
  const [interacciones, setInteracciones] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    cliente_id: '',
    tipo: 'nota',
    titulo: '',
    descripcion: '',
    fecha_hora: '',
    duracion_minutos: '',
    resultado: '',
  });

  const loadClientes = useCallback(async () => {
    try {
      const r = await clientesAPI.getAll(true);
      setClientes(Array.isArray(r.data) ? r.data : []);
    } catch {
      setClientes([]);
    }
  }, []);

  const loadInteracciones = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const filters = {};
      if (filtroTipo) filters.tipo = filtroTipo;
      if (filtroCliente) filters.cliente_id = filtroCliente;
      if (filtroFechaDesde) filters.fecha_desde = filtroFechaDesde;
      if (filtroFechaHasta) filters.fecha_hasta = filtroFechaHasta;
      const r = await interaccionesAPI.getAll(filters);
      const data = r.data?.data ?? r.data ?? [];
      setInteracciones(Array.isArray(data) ? data : []);
    } catch {
      setInteracciones([]);
      setError('Error al cargar interacciones');
    } finally {
      setLoading(false);
    }
  }, [filtroTipo, filtroCliente, filtroFechaDesde, filtroFechaHasta]);

  useEffect(() => {
    loadClientes();
    loadInteracciones();
  }, [loadClientes, loadInteracciones]);

  const handleSave = async () => {
    if (!form.cliente_id || !form.titulo.trim()) {
      setError('Cliente y título son obligatorios');
      return;
    }
    try {
      setError(null);
      await interaccionesAPI.create({
        ...form,
        duracion_minutos: form.duracion_minutos ? Number(form.duracion_minutos) : null,
        fecha_hora: form.fecha_hora || new Date().toISOString().slice(0, 16).replace('T', ' '),
      });
      setSuccess('Interacción creada');
      setShowForm(false);
      setForm({ cliente_id: '', tipo: 'nota', titulo: '', descripcion: '', fecha_hora: '', duracion_minutos: '', resultado: '' });
      loadInteracciones();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta interacción?')) return;
    try {
      await interaccionesAPI.delete(id);
      setSuccess('Interacción eliminada');
      loadInteracciones();
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Error al eliminar');
    }
  };

  const getClienteNombre = (item) => {
    if (item.cliente_nombre) return `${item.cliente_nombre} ${item.cliente_apellidos || ''}`.trim();
    const c = clientes.find(x => x.id === item.cliente_id);
    return c ? `${c.nombre} ${c.apellidos}` : '';
  };

  return (
    <div className="interacciones">
      <div className="page-header">
        <h2>Interacciones</h2>
        <button type="button" className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancelar' : '+ Nueva interacción'}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {showForm && (
        <div className="interaccion-form-card">
          <h3>Nueva interacción</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Cliente *</label>
              <select value={form.cliente_id} onChange={e => setForm(f => ({ ...f, cliente_id: e.target.value }))}>
                <option value="">Seleccionar...</option>
                {clientes.map(c => (
                  <option key={c.id} value={c.id}>{c.nombre} {c.apellidos}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Tipo</label>
              <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
                {TIPOS.map(t => (
                  <option key={t} value={t}>{TIPO_ICONS[t] || ''} {t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Título *</label>
            <input
              type="text"
              value={form.titulo}
              onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
              placeholder="Ej: Llamada de seguimiento"
            />
          </div>
          <div className="form-group">
            <label>Descripción</label>
            <textarea
              value={form.descripcion}
              onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
              rows={3}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Fecha y hora</label>
              <input
                type="datetime-local"
                value={form.fecha_hora}
                onChange={e => setForm(f => ({ ...f, fecha_hora: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Duración (min)</label>
              <input
                type="number"
                min="0"
                value={form.duracion_minutos}
                onChange={e => setForm(f => ({ ...f, duracion_minutos: e.target.value }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Resultado</label>
            <input
              type="text"
              value={form.resultado}
              onChange={e => setForm(f => ({ ...f, resultado: e.target.value }))}
              placeholder="Ej: Cliente interesado, pendiente de propuesta"
            />
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-primary" onClick={handleSave}>Guardar</button>
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
          </div>
        </div>
      )}

      <div className="interacciones-filters">
        <div className="filters-row">
          <div className="filter-group">
            <label>Tipo:</label>
            <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
              <option value="">Todos</option>
              {TIPOS.map(t => (
                <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Cliente:</label>
            <select value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}>
              <option value="">Todos</option>
              {clientes.map(c => (
                <option key={c.id} value={c.id}>{c.nombre} {c.apellidos}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Desde:</label>
            <input type="date" value={filtroFechaDesde} onChange={e => setFiltroFechaDesde(e.target.value)} />
          </div>
          <div className="filter-group">
            <label>Hasta:</label>
            <input type="date" value={filtroFechaHasta} onChange={e => setFiltroFechaHasta(e.target.value)} />
          </div>
        </div>
      </div>

      {loading ? (
        <p>Cargando interacciones...</p>
      ) : interacciones.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <p style={{ fontSize: '18px', marginBottom: '10px' }}>No hay interacciones registradas</p>
          <p style={{ fontSize: '14px' }}>Las interacciones se crean automáticamente al gestionar citas, sesiones, facturas y oportunidades.</p>
        </div>
      ) : (
        <div className="interacciones-timeline">
          {interacciones.map(i => (
            <div key={i.id} className={`interaccion-item tipo-${i.tipo}`}>
              <div className="interaccion-icon">{TIPO_ICONS[i.tipo] || '📌'}</div>
              <div className="interaccion-body">
                <div className="interaccion-header">
                  <strong>{i.titulo}</strong>
                  <span className={`tipo-badge tipo-${i.tipo}`}>{i.tipo}</span>
                  <span className="interaccion-fecha">
                    {i.fecha_hora ? new Date(i.fecha_hora).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </div>
                <div className="interaccion-cliente">{getClienteNombre(i)}</div>
                {i.descripcion && <p className="interaccion-desc">{i.descripcion}</p>}
                {i.resultado && <p className="interaccion-resultado"><strong>Resultado:</strong> {i.resultado}</p>}
                {i.duracion_minutos > 0 && <span className="interaccion-duracion">{i.duracion_minutos} min</span>}
                {!i.referencia_tipo && (
                  <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDelete(i.id)} style={{ marginTop: '6px' }}>
                    Eliminar
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Interacciones;
